import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { toast } from "react-hot-toast";
import { HiPlus, HiRefresh, HiTrash, HiChevronDown, HiChevronRight, HiPencil } from "react-icons/hi";
import { useSelector } from "react-redux";

const CATEGORIES = [
  { key: "deliverables", label: "Deliverables" },
  { key: "skills", label: "Skills" },
  { key: "culture", label: "Culture" },
];

const SENIORITY_LEVELS = ["Junior", "Mid", "Senior", "Lead"];

function emptyTemplate() {
  const cat = () => ({ talent_bar: { Junior: 2, Mid: 3, Senior: 4, Lead: 4 }, subcategories: [] });
  return { title: "", deliverables: cat(), skills: cat(), culture: cat() };
}

function TemplateModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState(JSON.parse(JSON.stringify(template)));
  const [saving, setSaving] = useState(false);
  const [expandedCriteria, setExpandedCriteria] = useState({});

  function toggleCriteria(criteriaGroupKey) {
    setExpandedCriteria((prev) => ({ ...prev, [criteriaGroupKey]: !prev[criteriaGroupKey] }));
  }

  async function handleSave() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const { ok } = form._id ? await api.put(`/notation_template/${form._id}`, form) : await api.post("/notation_template", form);
      if (!ok) return toast.error("Failed to save");
      toast.success("Saved!");
      onSaved();
    } catch (error) {
      console.error(error);
      toast.error("Error saving template");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="absolute top-0 bottom-0 left-0 right-0 bg-[#00000066] flex justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-xl p-6 h-fit my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{form._id ? "Edit Template" : "New Template"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Title */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Developer Template"
          />
        </div>

        {/* Categories */}
        {CATEGORIES.map(({ key: categoryKey, label: categoryLabel }) => (
          <div key={categoryKey} className="mb-4 border border-gray-200 rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-4">{categoryLabel}</h4>

            {/* Talent Bar */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Talent Bar (expected score by seniority)</p>
              <div className="flex gap-4">
                {SENIORITY_LEVELS.map((seniorityLevel) => (
                  <div key={seniorityLevel} className="flex flex-col items-center gap-1">
                    <label className="text-xs text-gray-500">{seniorityLevel}</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:border-blue-500"
                      value={form[categoryKey]?.talent_bar?.[seniorityLevel] ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          [categoryKey]: { ...f[categoryKey], talent_bar: { ...f[categoryKey].talent_bar, [seniorityLevel]: Number(e.target.value) } },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Subcategories */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Subcategories</p>
              <div className="space-y-2">
                {(form[categoryKey]?.subcategories || []).map((subcategory, subcategoryIndex) => {
                  const criteriaGroupKey = `${categoryKey}-${subcategoryIndex}`;
                  const isExpanded = !!expandedCriteria[criteriaGroupKey];
                  return (
                    <div key={subcategoryIndex} className="border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2 px-3 py-2">
                        <button onClick={() => toggleCriteria(criteriaGroupKey)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                          {isExpanded ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
                        </button>
                        <input
                          className="flex-1 bg-transparent text-sm text-gray-900 focus:outline-none"
                          value={subcategory.name}
                          onChange={(e) =>
                            setForm((f) => {
                              const subcategories = [...f[categoryKey].subcategories];
                              subcategories[subcategoryIndex] = { ...subcategories[subcategoryIndex], name: e.target.value };
                              return { ...f, [categoryKey]: { ...f[categoryKey], subcategories } };
                            })
                          }
                          placeholder="Subcategory name"
                        />
                        <button
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              [categoryKey]: { ...f[categoryKey], subcategories: f[categoryKey].subcategories.filter((subcategory, index) => index !== subcategoryIndex) },
                            }))
                          }
                          className="text-red-400 hover:text-red-600 flex-shrink-0">
                          <HiTrash className="w-4 h-4" />
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-gray-200 pt-3 space-y-2">
                          <p className="text-xs text-gray-400 mb-2">Criteria per score level</p>
                          {[1, 2, 3, 4, 5].map((scoreLevel) => (
                            <div key={scoreLevel} className="flex items-start gap-2">
                              <span className="text-xs font-semibold text-gray-500 w-4 pt-1.5 flex-shrink-0">{scoreLevel}</span>
                              <textarea
                                rows={2}
                                className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 resize-none focus:outline-none focus:border-blue-400"
                                value={subcategory.criteria?.[scoreLevel] || ""}
                                onChange={(e) =>
                                  setForm((f) => {
                                    const subcategories = [...f[categoryKey].subcategories];
                                    subcategories[subcategoryIndex] = {
                                      ...subcategories[subcategoryIndex],
                                      criteria: { ...subcategories[subcategoryIndex].criteria, [scoreLevel]: e.target.value },
                                    };
                                    return { ...f, [categoryKey]: { ...f[categoryKey], subcategories } };
                                  })
                                }
                                placeholder={`Description for score ${scoreLevel}…`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      [categoryKey]: {
                        ...f[categoryKey],
                        subcategories: [...(f[categoryKey].subcategories || []), { name: "", criteria: {} }],
                      },
                    }))
                  }
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1">
                  <HiPlus className="w-3 h-3" /> Add subcategory
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings({ cycle, fetchCycles }) {
  const { businessUnit } = useSelector((state) => state.Auth);
  const business_unit_id = businessUnit?._id;
  const business_unit_name = businessUnit?.name;

  const [reviewSummaries, setReviewSummaries] = useState([]);
  const [loadingReviewSummaries, setLoadingReviewSummaries] = useState(true);

  const [objectives, setObjectives] = useState([]);
  const [savingObjectives, setSavingObjectives] = useState(false);
  const [completingCycle, setCompletingCycle] = useState(false);
  const [generatingReviews, setGeneratingReviews] = useState(false);

  const [newCycleQuarter, setNewCycleQuarter] = useState("Q1");
  const [newCycleYear, setNewCycleYear] = useState(new Date().getFullYear());
  const [creatingCycle, setCreatingCycle] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    fetchReviewSummaries();
    fetchTemplates();
    fetchUsers();
  }, [business_unit_id]);

  // Keep the editable objectives in sync with the selected cycle coming from context
  useEffect(() => {
    setObjectives(cycle?.objectives?.length ? cycle.objectives : [""]);
  }, [cycle]);

  async function fetchReviewSummaries() {
    if (!business_unit_id) return;
    setLoadingReviewSummaries(true);
    try {
      const { data, ok } = await api.get(`/performance_review/summaries/${business_unit_id}`);
      if (!ok) return;
      setReviewSummaries(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingReviewSummaries(false);
    }
  }

  async function handleSaveObjectives() {
    setSavingObjectives(true);
    try {
      const { ok } = await api.put(`/cycle/${cycle._id}`, { objectives: objectives });
      if (!ok) return toast.error("Failed to save objectives");
      toast.success("Objectives saved");
      fetchCycles();
    } catch (error) {
      console.error(error);
      toast.error("Error saving objectives");
    } finally {
      setSavingObjectives(false);
    }
  }

  async function handleCompleteCycle() {
    if (!window.confirm("Mark this cycle as completed? This will lock the objectives and start the review generation window.")) return;
    setCompletingCycle(true);
    try {
      const { ok } = await api.put(`/cycle/${cycle._id}`, { objectives: objectives, status: "completed" });
      if (!ok) return toast.error("Failed to complete cycle");
      toast.success("Cycle marked as completed");
      fetchCycles();
    } catch (error) {
      console.error(error);
      toast.error("Error completing cycle");
    } finally {
      setCompletingCycle(false);
    }
  }

  async function handleGenerateReviews() {
    if (cycle.status !== "completed") return toast.error("Validez d'abord les objectifs du cycle");
    if (!window.confirm("Les templates de notation sont-ils à jour et bien assignés aux bons utilisateurs ?")) return;
    setGeneratingReviews(true);
    try {
      const { ok, data } = await api.post("/performance_review/generate", { cycle_id: cycle._id });
      if (!ok) return toast.error("Failed to generate reviews");
      toast.success(`${data.created.length} reviews generated`);
      fetchReviewSummaries();
    } catch (error) {
      console.error(error);
      if (error?.code === "MISSING_TEMPLATE_FOR_USERS") return toast.error("Missing notation template for: " + error.users.join(", "));
      toast.error("Error generating reviews");
    } finally {
      setGeneratingReviews(false);
    }
  }

  async function handleCreateCycle() {
    setCreatingCycle(true);
    try {
      const { ok } = await api.post("/cycle", { business_unit_id, business_unit_name, quarter: newCycleQuarter, year: Number(newCycleYear) });
      if (!ok) return toast.error("Failed to create cycle");
      toast.success("Cycle created");
      fetchCycles();
    } catch (error) {
      console.error(error);
      if (error?.code === "CYCLE_ALREADY_EXISTS") return toast.error("This cycle already exists");
      toast.error("Error creating cycle");
    } finally {
      setCreatingCycle(false);
    }
  }

  async function fetchTemplates() {
    setLoadingTemplates(true);
    try {
      const { data, ok } = await api.post("/notation_template/search", { business_unit_id });
      if (!ok) return;
      setTemplates(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function handleDeleteTemplate(templateId, e) {
    e.stopPropagation();
    if (!window.confirm("Delete this notation template? This action cannot be undone.")) return;
    try {
      const { ok } = await api.remove(`/notation_template/${templateId}`);
      if (!ok) return toast.error("Failed to delete");
      toast.success("Deleted");
      fetchTemplates();
    } catch (error) {
      console.error(error);
      toast.error("Error deleting template");
    }
  }

  async function fetchUsers() {
    if (!business_unit_id) return;
    setLoadingUsers(true);
    try {
      const { data, ok } = await api.post("/user/search", { business_unit_id, status: "active", exclude_daily_freelancers: true, per_page: 200 });
      if (!ok) return;
      setUsers(data.users);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingUsers(false);
    }
  }

  async function handleAssignTemplate(userId, templateId) {
    try {
      const { ok } = await api.put(`/user/${userId}`, { notation_template_id: templateId });
      if (!ok) return toast.error("Failed to assign template");
      toast.success("Template assigned");
      fetchUsers();
    } catch (error) {
      console.error(error);
      toast.error("Error assigning template");
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Performance Settings</h2>
        <p className="text-sm text-gray-600 mt-1">Manage review cycles and notation templates</p>
      </div>

      {/* Current Cycle */}
      {cycle && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-gray-900">
                Current Cycle — {cycle.quarter} {cycle.year}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">Company objectives for {business_unit_name || "this team"}.</p>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${cycle.status === "draft" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>
              {cycle.status}
            </span>
          </div>

          <div className="space-y-2 mb-4">
            {objectives.map((objective, objectiveIndex) => (
              <div key={objectiveIndex} className="flex items-center gap-2">
                <input
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  value={objective}
                  disabled={cycle.status !== "draft"}
                  onChange={(e) =>
                    setObjectives((prev) => prev.map((existingObjective, index) => (index === objectiveIndex ? e.target.value : existingObjective)))
                  }
                  placeholder="e.g. Ship the new billing flow"
                />
                {cycle.status === "draft" && (
                  <button
                    onClick={() => setObjectives((prev) => prev.filter((existingObjective, index) => index !== objectiveIndex))}
                    className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <HiTrash className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {cycle.status === "draft" && (
            <div className="flex items-center justify-between">
              <button onClick={() => setObjectives((prev) => [...prev, ""])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                <HiPlus className="w-3 h-3" /> Add objective
              </button>
              <div className="flex items-center gap-3">
                <button onClick={handleSaveObjectives} disabled={savingObjectives} className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-50">
                  {savingObjectives ? "Saving…" : "Save objectives"}
                </button>
                <button
                  onClick={handleCompleteCycle}
                  disabled={completingCycle}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                  {completingCycle ? "Completing…" : "Mark as completed"}
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={handleGenerateReviews}
              disabled={generatingReviews}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              {generatingReviews ? "Generating…" : "Generate reviews"}
            </button>
          </div>
        </div>
      )}

      {/* Review Cycles */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">Review Cycles</h3>
          <button onClick={fetchReviewSummaries} className="text-gray-600 hover:text-gray-900">
            <HiRefresh className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-6">Quarterly performance review cycles for the {business_unit_name || "selected"} team.</p>

        {/* New Cycle */}
        <div className="flex items-center gap-3 mb-6">
          <select
            className="border border-gray-300 rounded-lg pl-3 pr-8 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
            value={newCycleQuarter}
            onChange={(e) => setNewCycleQuarter(e.target.value)}>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </select>
          <select
            className="border border-gray-300 rounded-lg pl-3 pr-8 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
            value={newCycleYear}
            onChange={(e) => setNewCycleYear(Number(e.target.value))}>
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            onClick={handleCreateCycle}
            disabled={creatingCycle}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <HiPlus className="w-4 h-4" /> {creatingCycle ? "Creating…" : "New cycle"}
          </button>
        </div>

        {/* Existing Cycles */}
        {loadingReviewSummaries ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : reviewSummaries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No review cycles created yet.</div>
        ) : (
          <div className="space-y-3">
            {reviewSummaries.map((summary) => (
              <div key={`${summary.quarter}-${summary.year}`} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{summary.cycle_name}</p>
                  <p className="text-xs text-gray-500">{summary.completed_count}/{summary.review_count} completed</p>
                </div>
                <div className="flex items-center gap-4">
                  {summary.avg_score && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-blue-600">{summary.avg_score}</p>
                      <p className="text-xs text-gray-500">avg score</p>
                    </div>
                  )}
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(summary.completed_count / summary.review_count) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notation Templates */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-medium text-gray-900">Notation Templates</h3>
            <p className="text-sm text-gray-600 mt-0.5">Define subcategories and talent bars for each seniority level.</p>
          </div>
          <button onClick={() => setSelectedTemplate({ ...emptyTemplate(), business_unit_id, business_unit_name })} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <HiPlus className="w-4 h-4" /> New Template
          </button>
        </div>

        {loadingTemplates ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No templates yet. Create your first one.</div>
        ) : (
          <div className="space-y-2">
            {templates.map((template) => (
              <div
                key={template._id}
                onClick={() => setSelectedTemplate(template)}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors group">
                <div>
                  <p className="font-medium text-gray-900">{template.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Deliverables: {template.deliverables?.subcategories?.length ?? 0} ·{" "}
                    Skills: {template.skills?.subcategories?.length ?? 0} ·{" "}
                    Culture: {template.culture?.subcategories?.length ?? 0}
                  </p>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-blue-600 text-sm flex items-center gap-1">
                    <HiPencil className="w-4 h-4" /> Edit
                  </span>
                  <button onClick={(e) => handleDeleteTemplate(template._id, e)} className="text-red-400 hover:text-red-600 ml-2">
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team — assign a template to each employee */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="font-medium text-gray-900">Team</h3>
          <p className="text-sm text-gray-600 mt-0.5">Assign a notation template to each member of the {business_unit_name || "selected"} team.</p>
        </div>

        {loadingUsers ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No team members found.</div>
        ) : (
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">{user.name?.charAt(0)}</div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.position || user.job_title || "—"}</p>
                  </div>
                </div>
                <select
                  className="border border-gray-300 rounded-lg pl-3 pr-8 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
                  value={user.notation_template_id || ""}
                  onChange={(e) => handleAssignTemplate(user._id, e.target.value)}>
                  <option value="">No template</option>
                  {templates.map((template) => (
                    <option key={template._id} value={template._id}>{template.title}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {selectedTemplate && (
        <TemplateModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onSaved={() => {
            setSelectedTemplate(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
}
